import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { collection, doc, getDocs, orderBy, query, updateDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import {
    Block,
    Chapter,
    Section,
    createBlock,
    createBook,
    createChapter,
    createSection,
    listBlocksForBook,
    listBooksForShikaku,
    listChaptersForBook,
    listSectionsForBook,
} from '@/utils/bookStructure';
import DynamicBlockFactory from './components/DynamicBlockFactory';
import StructureNavigator from './components/StructureNavigator';
import { useRealtimeAutoSaver } from './hooks/useRealtimeAutoSaver';
import {
    BookStructureContext,
    bookStructureReducer,
    initialBookStructureState,
} from './store';

const ORDER_STEP = 1000;

const getOrderBetween = (prev?: number, next?: number) => {
    if (prev === undefined && next === undefined) return ORDER_STEP;
    if (prev === undefined) return next! - ORDER_STEP;
    if (next === undefined) return prev + ORDER_STEP;
    const mid = Math.floor((prev + next) / 2);
    if (mid === prev || mid === next) return prev + 1;
    return mid;
};

const sortByOrder = <T extends { order: number }>(items: T[]) => [...items].sort((a, b) => a.order - b.order);

const getDefaultBlockContent = (type: Block['type']) => {
    switch (type) {
        case 'paragraph':
        case 'note':
        case 'subheading':
            return { text: '' };
        case 'list':
            return { items: [''] };
        case 'table':
            return { rows: [['', '']] };
        case 'image':
            return { url: '', caption: '', alt: '' };
        default:
            return { text: '' };
    }
};

type BlockEditorCardProps = {
    block: Block;
    bookId: string;
    onContentChange: (blockId: string, content: Record<string, unknown>) => void;
    onInsertBelow: (blockId: string) => void;
};

const BlockEditorCard = ({ block, bookId, onContentChange, onInsertBelow }: BlockEditorCardProps) => {
    const blockRef = useMemo(() => doc(db, 'blocks', block.id), [block.id]);
    useRealtimeAutoSaver(blockRef, { content: block.content, updatedAt: new Date() }, 800);

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs font-semibold text-gray-500">{block.type}</div>
                <div className="text-xs text-gray-400">order: {block.order}</div>
                <button
                    type="button"
                    className="rounded-md border border-emerald-200 px-2 py-1 text-xs text-emerald-600"
                    onClick={() => onInsertBelow(block.id)}
                >
                    下に追加
                </button>
            </div>
            <DynamicBlockFactory
                block={block}
                storagePath={`book-structures/${bookId}/blocks/${block.id}`}
                onChange={(content) => onContentChange(block.id, content)}
            />
        </div>
    );
};

const SectionTitleEditor = ({
    title,
    onChange,
}: {
    title: string;
    onChange: (value: string) => void;
}) => (
    <input
        className="w-full border-b border-gray-200 px-1 py-1 text-base font-semibold focus:outline-none"
        value={title}
        onChange={(event) => onChange(event.target.value)}
        placeholder="セクションタイトル"
    />
);

export default function BookStructurePage() {
    const [state, dispatch] = useReducer(bookStructureReducer, initialBookStructureState);
    const [bookTitle, setBookTitle] = useState('');
    const [bookEdition, setBookEdition] = useState('1.0');
    const [bookPublished, setBookPublished] = useState(false);
    const [bookEditingId, setBookEditingId] = useState<string | null>(null);
    const [bookEditingTitle, setBookEditingTitle] = useState('');
    const [bookEditingEdition, setBookEditingEdition] = useState('1.0');
    const [bookEditingPublished, setBookEditingPublished] = useState(false);
    const [chapterTitle, setChapterTitle] = useState('');
    const [sectionTitle, setSectionTitle] = useState('');
    const [newBlockType, setNewBlockType] = useState<Block['type']>('paragraph');
    const [sectionTitleDraft, setSectionTitleDraft] = useState('');
    const [previewFontFamily, setPreviewFontFamily] = useState('Noto Sans JP');
    const [previewFontSize, setPreviewFontSize] = useState('14');

    const selectedBook = useMemo(
        () => state.books.find((book) => book.id === state.selectedBookId) ?? null,
        [state.books, state.selectedBookId]
    );

    const selectedSection = useMemo(
        () => state.sections.find((section) => section.id === state.selectedSectionId) ?? null,
        [state.sections, state.selectedSectionId]
    );

    useEffect(() => {
        if (selectedSection) {
            setSectionTitleDraft(selectedSection.title);
        }
    }, [selectedSection?.id, selectedSection?.title]);

    const sectionRef = useMemo(
        () => (selectedSection ? doc(db, 'sections', selectedSection.id) : null),
        [selectedSection?.id]
    );
    useRealtimeAutoSaver(sectionRef, { title: sectionTitleDraft }, 800);

    const bookChapters = useMemo(() => sortByOrder(state.chapters), [state.chapters]);
    const sectionBlocks = useMemo(
        () =>
            sortByOrder(
                state.blocks.filter((block) => block.sectionRef?.id === state.selectedSectionId)
            ),
        [state.blocks, state.selectedSectionId]
    );

    const loadShikakuOptions = useCallback(async () => {
        dispatch({ type: 'loading', payload: true });
        try {
            const snapshot = await getDocs(query(collection(db, 'Shikaku'), orderBy('ShikakuName')));
            const options = snapshot.docs.map((docSnap) => ({
                id: docSnap.id,
                name: docSnap.data().ShikakuName || docSnap.id,
            }));
            dispatch({ type: 'shikakuOptions', payload: options });
            if (!state.selectedShikakuId && options.length > 0) {
                dispatch({ type: 'selectedShikakuId', payload: options[0].id });
            }
        } catch (err) {
            console.error('Failed to load shikaku options:', err);
            dispatch({ type: 'error', payload: '資格一覧の取得に失敗しました。' });
        } finally {
            dispatch({ type: 'loading', payload: false });
        }
    }, [state.selectedShikakuId]);

    const loadBooks = useCallback(async () => {
        if (!state.selectedShikakuId) return;
        dispatch({ type: 'loading', payload: true });
        try {
            const list = await listBooksForShikaku(state.selectedShikakuId);
            const books = list.map((book) => ({
                id: book.id,
                title: book.title,
                edition: book.edition,
                isPublished: book.isPublished,
            }));
            dispatch({ type: 'books', payload: books });
            if (!state.selectedBookId && books.length > 0) {
                dispatch({ type: 'selectedBookId', payload: books[0].id });
            }
        } catch (err) {
            console.error('Failed to load books:', err);
            dispatch({ type: 'error', payload: '冊子一覧の取得に失敗しました。' });
        } finally {
            dispatch({ type: 'loading', payload: false });
        }
    }, [state.selectedShikakuId, state.selectedBookId]);

    const loadStructure = useCallback(async () => {
        if (!state.selectedBookId) return;
        dispatch({ type: 'loading', payload: true });
        try {
            const bookRef = doc(db, 'books', state.selectedBookId);
            const [chapterList, sectionList, blockList] = await Promise.all([
                listChaptersForBook(bookRef),
                listSectionsForBook(bookRef),
                listBlocksForBook(bookRef),
            ]);
            dispatch({ type: 'chapters', payload: chapterList });
            dispatch({ type: 'sections', payload: sectionList });
            dispatch({ type: 'blocks', payload: blockList });
            if (!state.selectedChapterId && chapterList.length > 0) {
                dispatch({ type: 'selectedChapterId', payload: chapterList[0].id });
            }
            if (!state.selectedSectionId && sectionList.length > 0) {
                dispatch({ type: 'selectedSectionId', payload: sectionList[0].id });
            }
        } catch (err) {
            console.error('Failed to load structure:', err);
            dispatch({ type: 'error', payload: '構造データの取得に失敗しました。' });
        } finally {
            dispatch({ type: 'loading', payload: false });
        }
    }, [state.selectedBookId, state.selectedChapterId, state.selectedSectionId]);

    useEffect(() => {
        void loadShikakuOptions();
    }, [loadShikakuOptions]);

    useEffect(() => {
        void loadBooks();
    }, [loadBooks]);

    useEffect(() => {
        void loadStructure();
    }, [loadStructure]);

    const handleCreateBook = async () => {
        if (!state.selectedShikakuId || !bookTitle.trim()) {
            dispatch({ type: 'error', payload: '資格と冊子タイトルを入力してください。' });
            return;
        }
        dispatch({ type: 'loading', payload: true });
        try {
            const bookDoc = await createBook({
                shikakuId: state.selectedShikakuId,
                title: bookTitle.trim(),
                edition: bookEdition.trim() || '1.0',
                isPublished: bookPublished,
            });
            const nextBooks = [
                {
                    id: bookDoc.id,
                    title: bookTitle.trim(),
                    edition: bookEdition.trim() || '1.0',
                    isPublished: bookPublished,
                },
                ...state.books,
            ];
            dispatch({ type: 'books', payload: nextBooks });
            dispatch({ type: 'selectedBookId', payload: bookDoc.id });
            setBookTitle('');
            setBookEdition('1.0');
            setBookPublished(false);
        } catch (err) {
            console.error('Failed to create book:', err);
            dispatch({ type: 'error', payload: '冊子の作成に失敗しました。' });
        } finally {
            dispatch({ type: 'loading', payload: false });
        }
    };

    const handleStartEditBook = (book: { id: string; title: string; edition?: string; isPublished: boolean }) => {
        setBookEditingId(book.id);
        setBookEditingTitle(book.title);
        setBookEditingEdition(book.edition || '1.0');
        setBookEditingPublished(book.isPublished);
    };

    const handleSaveBook = async () => {
        if (!bookEditingId || !bookEditingTitle.trim()) {
            dispatch({ type: 'error', payload: '冊子タイトルを入力してください。' });
            return;
        }
        dispatch({ type: 'loading', payload: true });
        try {
            await updateDoc(doc(db, 'books', bookEditingId), {
                title: bookEditingTitle.trim(),
                edition: bookEditingEdition.trim() || '1.0',
                isPublished: bookEditingPublished,
                updatedAt: new Date(),
            });
            dispatch({
                type: 'books',
                payload: state.books.map((book) =>
                    book.id === bookEditingId
                        ? {
                              ...book,
                              title: bookEditingTitle.trim(),
                              edition: bookEditingEdition.trim() || '1.0',
                              isPublished: bookEditingPublished,
                          }
                        : book
                ),
            });
            setBookEditingId(null);
        } catch (err) {
            console.error('Failed to update book:', err);
            dispatch({ type: 'error', payload: '冊子の更新に失敗しました。' });
        } finally {
            dispatch({ type: 'loading', payload: false });
        }
    };

    const handleCreateChapter = async () => {
        if (!state.selectedBookId || !chapterTitle.trim()) {
            dispatch({ type: 'error', payload: '章タイトルを入力してください。' });
            return;
        }
        dispatch({ type: 'loading', payload: true });
        try {
            const bookRef = doc(db, 'books', state.selectedBookId);
            const order = getOrderBetween(bookChapters.at(-1)?.order, undefined);
            const chapterDoc = await createChapter({ bookRef, title: chapterTitle.trim(), order, breakBefore: false });
            dispatch({
                type: 'chapters',
                payload: [...state.chapters, { id: chapterDoc.id, bookRef, title: chapterTitle.trim(), order }],
            });
            dispatch({ type: 'selectedChapterId', payload: chapterDoc.id });
            setChapterTitle('');
        } catch (err) {
            console.error('Failed to create chapter:', err);
            dispatch({ type: 'error', payload: '章の作成に失敗しました。' });
        } finally {
            dispatch({ type: 'loading', payload: false });
        }
    };

    const handleCreateSection = async () => {
        if (!state.selectedBookId || !state.selectedChapterId || !sectionTitle.trim()) {
            dispatch({ type: 'error', payload: '対象章とセクションタイトルを入力してください。' });
            return;
        }
        dispatch({ type: 'loading', payload: true });
        try {
            const bookRef = doc(db, 'books', state.selectedBookId);
            const chapterRef = doc(db, 'chapters', state.selectedChapterId);
            const sectionList = state.sections.filter((section) => section.chapterRef.id === state.selectedChapterId);
            const order = getOrderBetween(sectionList.at(-1)?.order, undefined);
            const sectionDoc = await createSection({ bookRef, chapterRef, title: sectionTitle.trim(), order });
            dispatch({
                type: 'sections',
                payload: [...state.sections, { id: sectionDoc.id, bookRef, chapterRef, title: sectionTitle.trim(), order }],
            });
            dispatch({ type: 'selectedSectionId', payload: sectionDoc.id });
            setSectionTitle('');
        } catch (err) {
            console.error('Failed to create section:', err);
            dispatch({ type: 'error', payload: 'セクションの作成に失敗しました。' });
        } finally {
            dispatch({ type: 'loading', payload: false });
        }
    };

    const handleCreateBlock = async (afterBlockId?: string) => {
        if (!state.selectedBookId || !state.selectedChapterId || !state.selectedSectionId) {
            dispatch({ type: 'error', payload: '対象章・セクションを選択してください。' });
            return;
        }
        const sorted = sortByOrder(
            state.blocks.filter((block) => block.sectionRef.id === state.selectedSectionId)
        );
        const currentIndex = afterBlockId ? sorted.findIndex((block) => block.id === afterBlockId) : sorted.length - 1;
        const prev = currentIndex >= 0 ? sorted[currentIndex]?.order : undefined;
        const next = sorted[currentIndex + 1]?.order;
        const order = getOrderBetween(prev, next);
        dispatch({ type: 'loading', payload: true });
        try {
            const bookRef = doc(db, 'books', state.selectedBookId);
            const chapterRef = doc(db, 'chapters', state.selectedChapterId);
            const sectionRef = doc(db, 'sections', state.selectedSectionId);
            const content = getDefaultBlockContent(newBlockType);
            const blockDoc = await createBlock({
                bookRef,
                chapterRef,
                sectionRef,
                type: newBlockType,
                order,
                content,
                options: {},
            });
            dispatch({
                type: 'blocks',
                payload: [...state.blocks, { id: blockDoc.id, bookRef, chapterRef, sectionRef, type: newBlockType, order, content }],
            });
        } catch (err) {
            console.error('Failed to create block:', err);
            dispatch({ type: 'error', payload: 'ブロックの作成に失敗しました。' });
        } finally {
            dispatch({ type: 'loading', payload: false });
        }
    };

    const handleBlockContentChange = (blockId: string, content: Record<string, unknown>) => {
        dispatch({
            type: 'blocks',
            payload: state.blocks.map((block) => (block.id === blockId ? { ...block, content } : block)),
        });
    };

    const handleReorderChapters = async (next: Chapter[]) => {
        dispatch({ type: 'chapters', payload: next });
        await Promise.all(
            next.map((chapter, index) =>
                updateDoc(doc(db, 'chapters', chapter.id), { order: (index + 1) * ORDER_STEP })
            )
        );
        dispatch({ type: 'chapters', payload: next.map((chapter, index) => ({ ...chapter, order: (index + 1) * ORDER_STEP })) });
    };

    const handleReorderSections = async (chapterId: string, next: Section[]) => {
        const updated = state.sections.map((section) => {
            const index = next.findIndex((item) => item.id === section.id);
            if (section.chapterRef.id !== chapterId || index === -1) return section;
            return { ...section, order: (index + 1) * ORDER_STEP };
        });
        dispatch({ type: 'sections', payload: updated });
        await Promise.all(
            next.map((section, index) => updateDoc(doc(db, 'sections', section.id), { order: (index + 1) * ORDER_STEP }))
        );
    };

    return (
        <BookStructureContext.Provider value={{ state, dispatch }}>
            <div className="min-h-screen bg-[#f6f7f8] text-[#111418]">
                <header className="sticky top-0 z-40 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3">
                    <div className="flex items-center gap-4">
                        <div className="rounded-lg bg-emerald-500 p-2 text-white">
                            <span className="text-lg">📘</span>
                        </div>
                        <div>
                            <h1 className="text-sm font-bold leading-tight">Booklet Builder</h1>
                            <p className="text-xs text-gray-500">
                                {selectedBook?.title ? `${selectedBook.title} - Draft` : '冊子を選択してください'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <button className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700">
                            Save
                        </button>
                        <button className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white">
                            Export PDF
                        </button>
                        <div className="h-9 w-9 rounded-full border border-gray-200 bg-gray-100" />
                    </div>
                </header>

                <div className="border-b border-gray-200 bg-white">
                    <div className="flex flex-wrap items-center gap-3 px-6 py-2 text-xs font-semibold text-gray-500">
                        {['ホーム', '挿入', 'デザイン', 'アニメーション', '表示'].map((tab) => (
                            <button key={tab} className="rounded px-2 py-1 hover:bg-gray-100">
                                {tab}
                            </button>
                        ))}
                        <span className="ml-auto flex items-center gap-2 text-[11px] text-emerald-600">
                            <span>●</span>
                            Autosaved
                        </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 px-6 pb-3 text-xs text-gray-500">
                        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                            <span className="text-gray-400">表示</span>
                            <span>100%</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button className="rounded border border-gray-200 px-3 py-2 hover:bg-gray-50">Undo</button>
                            <button className="rounded border border-gray-200 px-3 py-2 hover:bg-gray-50">Redo</button>
                        </div>
                        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                            <button className="rounded px-2 py-1 hover:bg-gray-100">Bold</button>
                            <button className="rounded px-2 py-1 hover:bg-gray-100">Italic</button>
                            <button className="rounded px-2 py-1 hover:bg-gray-100">Align</button>
                        </div>
                        <div className="ml-auto flex items-center gap-2 text-xs font-semibold text-gray-600">
                            Edit Mode
                        </div>
                    </div>
                    {state.error && (
                        <div className="px-6 pb-3">
                            <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
                                {state.error}
                            </div>
                        </div>
                    )}
                </div>

                <main className="flex h-[calc(100vh-152px)] overflow-hidden">
                    <aside className="w-72 border-r border-gray-200 bg-white">
                        <div className="border-b border-gray-100 p-4">
                            <h2 className="text-sm font-bold">スライド一覧</h2>
                            <p className="text-[11px] text-gray-500">セクションをスライドとして表示</p>
                        </div>
                        <div className="px-4 pt-4">
                            <button
                                type="button"
                                className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-xs font-semibold text-emerald-600"
                                onClick={handleCreateSection}
                                disabled={state.loading}
                            >
                                + 新しいスライド
                            </button>
                        </div>
                        <div className="mt-3 space-y-2 overflow-y-auto px-4 pb-4 text-xs max-h-[calc(100vh-280px)]">
                            {sortByOrder(state.sections).map((section, index) => (
                                <button
                                    key={section.id}
                                    type="button"
                                    onClick={() => dispatch({ type: 'selectedSectionId', payload: section.id })}
                                    className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left text-xs ${
                                        section.id === state.selectedSectionId
                                            ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                            : 'border-gray-100 text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="flex h-12 w-16 items-center justify-center rounded-md border border-gray-200 bg-white text-[10px] text-gray-400">
                                        {index + 1}
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-semibold text-gray-700">{section.title || '無題'}</div>
                                        <div className="text-[10px] text-gray-400">章: {section.chapterRef?.id ?? '-'}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </aside>

                    <section className="flex-1 overflow-y-auto bg-[#e9ecf1] p-8">
                        <div className="mx-auto flex max-w-[960px] flex-col gap-6">
                            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">A4キャンバス</h3>
                                        <p className="text-sm text-gray-500">プレビュー上でブロック編集できます。</p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3">
                                        <select
                                            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                                            value={previewFontFamily}
                                            onChange={(event) => setPreviewFontFamily(event.target.value)}
                                        >
                                            <option value="Noto Sans JP">Noto Sans JP</option>
                                            <option value="Yu Gothic">Yu Gothic</option>
                                            <option value="Hiragino Kaku Gothic ProN">Hiragino Kaku Gothic ProN</option>
                                            <option value="Meiryo">Meiryo</option>
                                            <option value="Times New Roman">Times New Roman</option>
                                        </select>
                                        <select
                                            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                                            value={previewFontSize}
                                            onChange={(event) => setPreviewFontSize(event.target.value)}
                                        >
                                            {['10', '11', '12', '13', '14', '16', '18', '20'].map((size) => (
                                                <option key={size} value={size}>
                                                    {size}px
                                                </option>
                                            ))}
                                        </select>
                                        <select
                                            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                                            value={newBlockType}
                                            onChange={(event) => setNewBlockType(event.target.value as Block['type'])}
                                        >
                                            <option value="paragraph">本文</option>
                                            <option value="subheading">中見出し</option>
                                            <option value="note">注釈</option>
                                            <option value="list">リスト</option>
                                            <option value="table">表</option>
                                            <option value="image">画像</option>
                                        </select>
                                        <button
                                            type="button"
                                            className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white"
                                            onClick={() => handleCreateBlock()}
                                        >
                                            ブロック追加
                                        </button>
                                    </div>
                                </div>
                                {selectedSection ? (
                                    <div className="flex justify-center">
                                        <div
                                            className="w-full max-w-[794px] rounded-sm bg-white p-10 shadow-2xl"
                                            style={{
                                                fontFamily: `"${previewFontFamily}", "Hiragino Kaku Gothic ProN", "Meiryo", sans-serif`,
                                                fontSize: `${previewFontSize}px`,
                                            }}
                                        >
                                            <SectionTitleEditor
                                                title={sectionTitleDraft}
                                                onChange={(value) => {
                                                    setSectionTitleDraft(value);
                                                    dispatch({
                                                        type: 'sections',
                                                        payload: state.sections.map((section) =>
                                                            section.id === selectedSection.id
                                                                ? { ...section, title: value }
                                                                : section
                                                        ),
                                                    });
                                                }}
                                            />
                                            <div className="mt-6 space-y-6">
                                                {sectionBlocks.length === 0 ? (
                                                    <div className="rounded-xl border-2 border-dashed border-gray-200 py-12 text-center text-sm text-gray-400">
                                                        クリックしてブロックを追加
                                                    </div>
                                                ) : (
                                                    sectionBlocks.map((block) => (
                                                        <div key={block.id} className="space-y-4">
                                                            <DynamicBlockFactory
                                                                block={block}
                                                                storagePath={`book-structures/${state.selectedBookId}/blocks/${block.id}`}
                                                                onChange={(content) => handleBlockContentChange(block.id, content)}
                                                            />
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-500">セクションを選択してください。</div>
                                )}
                            </div>
                        </div>
                    </section>

                    <aside className="w-80 border-l border-gray-200 bg-white">
                        <div className="border-b border-gray-100 p-4">
                            <h2 className="text-sm font-bold">編集パネル</h2>
                            <p className="text-[11px] text-gray-500">ブロックと構造を管理</p>
                        </div>
                        <div className="flex h-full flex-col overflow-y-auto px-4 pb-8 pt-4 space-y-6 text-xs text-gray-600">
                            <div className="space-y-2">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Text</p>
                                {(['paragraph', 'subheading'] as Block['type'][]).map((type) => (
                                    <button
                                        key={type}
                                        type="button"
                                        className="flex w-full items-center gap-2 rounded-lg border border-dashed border-gray-200 px-3 py-2 text-left text-xs font-semibold hover:border-emerald-400 hover:bg-emerald-50"
                                        onClick={() => {
                                            setNewBlockType(type);
                                            handleCreateBlock();
                                        }}
                                    >
                                        {type === 'paragraph' ? '本文' : '中見出し'}
                                    </button>
                                ))}
                            </div>
                            <div className="space-y-2">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Visuals</p>
                                {(['image', 'table', 'list', 'note'] as Block['type'][]).map((type) => (
                                    <button
                                        key={type}
                                        type="button"
                                        className="flex w-full items-center gap-2 rounded-lg border border-dashed border-gray-200 px-3 py-2 text-left text-xs font-semibold hover:border-emerald-400 hover:bg-emerald-50"
                                        onClick={() => {
                                            setNewBlockType(type);
                                            handleCreateBlock();
                                        }}
                                    >
                                        {type === 'image'
                                            ? '画像'
                                            : type === 'table'
                                              ? '表'
                                              : type === 'list'
                                                ? 'リスト'
                                                : '注釈'}
                                    </button>
                                ))}
                            </div>
                            <div className="space-y-2">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Structure</p>
                                <input
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs"
                                    value={chapterTitle}
                                    onChange={(event) => setChapterTitle(event.target.value)}
                                    placeholder="章タイトル"
                                />
                                <button
                                    type="button"
                                    className="w-full rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white"
                                    onClick={handleCreateChapter}
                                    disabled={state.loading}
                                >
                                    章を追加
                                </button>
                                <input
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs"
                                    value={sectionTitle}
                                    onChange={(event) => setSectionTitle(event.target.value)}
                                    placeholder="セクションタイトル"
                                />
                                <button
                                    type="button"
                                    className="w-full rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white"
                                    onClick={handleCreateSection}
                                    disabled={state.loading}
                                >
                                    セクションを追加
                                </button>
                            </div>
                            <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                                <h3 className="text-xs font-semibold text-gray-700">冊子を作成</h3>
                                <label className="space-y-1 text-[11px] font-medium text-gray-600">
                                    対象資格
                                    <select
                                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs"
                                        value={state.selectedShikakuId}
                                        onChange={(event) =>
                                            dispatch({ type: 'selectedShikakuId', payload: event.target.value })
                                        }
                                    >
                                        {state.shikakuOptions.map((option) => (
                                            <option key={option.id} value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <input
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs"
                                    value={bookTitle}
                                    onChange={(event) => setBookTitle(event.target.value)}
                                    placeholder="冊子タイトル"
                                />
                                <div className="flex items-center gap-3">
                                    <input
                                        className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-xs"
                                        value={bookEdition}
                                        onChange={(event) => setBookEdition(event.target.value)}
                                        placeholder="版"
                                    />
                                    <label className="flex items-center gap-2 text-[11px] text-gray-600">
                                        <input
                                            type="checkbox"
                                            checked={bookPublished}
                                            onChange={(event) => setBookPublished(event.target.checked)}
                                        />
                                        公開
                                    </label>
                                </div>
                                <button
                                    type="button"
                                    className="w-full rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white"
                                    onClick={handleCreateBook}
                                    disabled={state.loading}
                                >
                                    冊子を作成
                                </button>
                            </div>
                            <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-3">
                                <h3 className="text-xs font-semibold text-gray-700">冊子選択</h3>
                                <select
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs"
                                    value={state.selectedBookId}
                                    onChange={(event) =>
                                        dispatch({ type: 'selectedBookId', payload: event.target.value })
                                    }
                                >
                                    {state.books.map((book) => (
                                        <option key={book.id} value={book.id}>
                                            {book.title}
                                        </option>
                                    ))}
                                </select>
                                {selectedBook && (
                                    <div className="rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-500">
                                        版: {selectedBook.edition || '-'} / {selectedBook.isPublished ? '公開中' : '非公開'}
                                    </div>
                                )}
                            </div>
                            <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-3">
                                <h3 className="text-xs font-semibold text-gray-700">冊子一覧・編集</h3>
                                {state.books.length === 0 ? (
                                    <div className="text-[11px] text-gray-500">冊子がまだ登録されていません。</div>
                                ) : (
                                    <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                                        {state.books.map((book) => (
                                            <div
                                                key={book.id}
                                                className={`rounded-lg border px-3 py-2 text-[11px] ${
                                                    book.id === state.selectedBookId
                                                        ? 'border-emerald-300 bg-emerald-50'
                                                        : 'border-gray-100'
                                                }`}
                                            >
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <div className="font-semibold text-gray-700">{book.title}</div>
                                                    <button
                                                        type="button"
                                                        className="rounded-md border border-emerald-200 px-2 py-1 text-[11px] text-emerald-600"
                                                        onClick={() => handleStartEditBook(book)}
                                                    >
                                                        編集
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {bookEditingId && (
                                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3 space-y-2">
                                        <div className="text-[11px] font-semibold text-emerald-700">冊子情報を編集</div>
                                        <input
                                            className="rounded-lg border border-emerald-200 px-3 py-2 text-xs"
                                            value={bookEditingTitle}
                                            onChange={(event) => setBookEditingTitle(event.target.value)}
                                            placeholder="冊子タイトル"
                                        />
                                        <input
                                            className="rounded-lg border border-emerald-200 px-3 py-2 text-xs"
                                            value={bookEditingEdition}
                                            onChange={(event) => setBookEditingEdition(event.target.value)}
                                            placeholder="版"
                                        />
                                        <label className="flex items-center gap-2 text-[11px] text-emerald-700">
                                            <input
                                                type="checkbox"
                                                checked={bookEditingPublished}
                                                onChange={(event) => setBookEditingPublished(event.target.checked)}
                                            />
                                            公開する
                                        </label>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                className="rounded-md bg-emerald-600 px-3 py-1 text-[11px] font-semibold text-white"
                                                onClick={handleSaveBook}
                                                disabled={state.loading}
                                            >
                                                保存
                                            </button>
                                            <button
                                                type="button"
                                                className="rounded-md border border-emerald-200 px-3 py-1 text-[11px] text-emerald-700"
                                                onClick={() => setBookEditingId(null)}
                                            >
                                                キャンセル
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-3">
                                <h3 className="text-xs font-semibold text-gray-700">構造ナビゲーション</h3>
                                <StructureNavigator
                                    chapters={bookChapters}
                                    sections={sortByOrder(state.sections)}
                                    selectedChapterId={state.selectedChapterId}
                                    selectedSectionId={state.selectedSectionId}
                                    onSelectChapter={(id) => dispatch({ type: 'selectedChapterId', payload: id })}
                                    onSelectSection={(id) => dispatch({ type: 'selectedSectionId', payload: id })}
                                    onReorderChapters={handleReorderChapters}
                                    onReorderSections={handleReorderSections}
                                />
                            </div>
                        </div>
                    </aside>
                </main>
            </div>
        </BookStructureContext.Provider>
    );
}
